/* =====================================================================================
   What documents say about one record.

   THE POINT OF SEPARATING THIS FROM THE RECORD ITSELF. Everything above it on a competitor card
   is our judgement: our position, their strength, their weakness, written by somebody here.
   Everything in here is somebody else's words with a page number. A reader must never have to
   work out which is which, so they never share a container and the evidence always shows its
   source.

   COLLAPSED BY DEFAULT, COUNTED ALWAYS. A card carrying ten quotes buries the assessment that
   the card exists for. The count and the best source available are visible without opening it,
   which is enough to know whether it is worth opening.

   IT LOADS NOTHING UNTIL ASKED. One card is fine; twenty competitor cards each fetching their
   own quotes on mount is twenty round trips to render a number nobody looked at.
   ===================================================================================== */

import { useCallback, useEffect, useState } from 'react';
import { BACKEND } from '../lib/db';
import './recordevidence.css';

interface Row {
  claim_id: string; claim_type: string; qualifier: string | null; subject_name: string;
  value_text: string | null; unit: string | null; period: string | null;
  confidence: string; quote: string; page: number | null; automatic: boolean;
  document_title: string; publisher: string | null; published_on: string | null;
  source_url: string | null; source_rank: number; source_label: string;
  decided_by_name: string | null;
}

const TYPE_LABEL: Record<string, string> = {
  volume: 'Volume', price: 'Price', platform: 'Platform', incumbent: 'Incumbent',
  contract: 'Contract', headcount: 'Headcount', regulatory: 'Regulatory',
  guideline: 'Guideline', coverage: 'Coverage', personnel: 'People', other: 'Other',
};

export function RecordEvidence({ kind, id }: { kind: 'company' | 'site'; id: string }) {
  const [count, setCount] = useState<number | null>(null);
  const [bestRank, setBestRank] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<Row[] | null>(null);

  useEffect(() => {
    if (BACKEND !== 'supabase') { setCount(0); return; }
    let live = true;
    (async () => {
      const { supabase } = await import('../lib/supabase');
      const { data } = await supabase().schema('app')
        .from('v_record_evidence_counts')
        .select('claims,best_source_rank')
        .eq('resolved_type', kind).eq('resolved_id', id).maybeSingle();
      if (!live) return;
      setCount((data as { claims?: number } | null)?.claims ?? 0);
      setBestRank((data as { best_source_rank?: number } | null)?.best_source_rank ?? null);
    })();
    return () => { live = false; };
  }, [kind, id]);

  const expand = useCallback(async () => {
    setOpen((o) => !o);
    if (rows || BACKEND !== 'supabase') return;
    const { supabase } = await import('../lib/supabase');
    const { data } = await supabase().schema('app')
      .from('v_record_evidence').select('*')
      .eq('resolved_type', kind).eq('resolved_id', id)
      .order('source_rank').order('claim_type');
    setRows((data ?? []) as unknown as Row[]);
  }, [kind, id, rows]);

  if (!count) return null;

  return (
    <div className={`re-wrap${open ? ' open' : ''}`}>
      <button className="re-toggle" onClick={expand} aria-expanded={open}>
        <span className="re-chev" aria-hidden>{open ? '▾' : '▸'}</span>
        <strong>{count}</strong> from documents
        {bestRank != null && (
          <span className={`re-best r${Math.min(bestRank, 8)}`}>
            {bestRank <= 2 ? 'best source: authoritative'
              : bestRank <= 4 ? 'best source: official'
              : bestRank <= 6 ? 'best source: company material'
              : 'best source: press or hearsay'}
          </span>
        )}
      </button>

      {open && (
        rows === null
          ? <p className="re-loading">Loading…</p>
          : <ul className="re-list">
              {rows.map((r) => (
                <li key={r.claim_id}>
                  <div className="re-line">
                    <span className="re-type">{TYPE_LABEL[r.claim_type] ?? r.claim_type}</span>
                    <span className="re-val">
                      {r.value_text}
                      {r.qualifier && <span className="re-qual"> · {r.qualifier}</span>}
                      {r.period && <span className="re-qual"> · {r.period}</span>}
                    </span>
                  </div>
                  <blockquote>
                    {r.quote}
                    <cite>
                      {r.publisher && <>{r.publisher}, </>}{r.document_title}
                      {r.page != null && <> · p{r.page}</>}
                      {r.source_url && <> · <a href={r.source_url} target="_blank"
                                               rel="noreferrer">link</a></>}
                    </cite>
                  </blockquote>
                  <p className="re-meta">
                    <span className={`re-tier r${Math.min(r.source_rank, 8)}`}>{r.source_label}</span>
                    {r.confidence !== 'stated' && <span className="re-inf">{r.confidence}</span>}
                    <span className="re-who">
                      {r.automatic
                        ? 'accepted automatically'
                        : `accepted by ${r.decided_by_name ?? 'someone here'}`}
                    </span>
                  </p>
                </li>
              ))}
            </ul>
      )}
    </div>
  );
}
